import { useNuxtApp } from '#app'

/**
 * Returns a function that checks whether the current user can navigate to a
 * given path. The three auth plugins each expose `.access(path)` which returns
 * `true` when the route declares no requirement of that type. AND-ing all
 * three is the correct way to enforce every requirement at once.
 *
 * Use this anywhere you need to gate UI by destination (nav items, search
 * results, dynamic links). The same triad is enforced at navigation time by
 * `middleware/auth.global.ts`.
 */
export function useAccess(): (path: string) => boolean {
  const nuxtApp = useNuxtApp()
  // `$grants`, `$features`, `$gates` are typed at the plugin level but the
  // useNuxtApp result is inferred broadly; pin them to their concrete shapes.
  const $grants = nuxtApp.$grants as { access(path: string): boolean }
  const $features = nuxtApp.$features as { access(path: string): boolean }
  const $gates = nuxtApp.$gates as { access(path: string): boolean }
  return (path: string): boolean => {
    return $grants.access(path) && $features.access(path) && $gates.access(path)
  }
}
